/**
 * 우측 목차 (스크롤 추적).
 *
 * 제목은 렌더된 DOM에서 읽는다 — 마크다운 원문을 다시 파싱하면 rehype-slug가 만든 앵커 id와
 * 어긋날 수 있고, 콜아웃처럼 별도 렌더 경로를 탄 제목을 놓친다.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { TOC_ROOT_MARGIN, isScrolledToBottom } from './manualTocGeometry';

/**
 * 실제로 스크롤하는 조상 요소를 찾는다.
 *
 * admin Layout은 window가 아니라 `<main class="overflow-y-auto">`가 스크롤한다.
 * 레이아웃이 바뀌어도 따라가도록 계산된 스타일로 탐색한다(선택자 하드코딩 금지).
 */
function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

interface TocEntry {
  id: string;
  text: string;
  level: 2 | 3;
}

interface ManualTocProps {
  /** 본문 컨테이너 — 이 안의 h2/h3를 목차로 만든다. */
  containerRef: RefObject<HTMLElement | null>;
  /** 문서가 바뀌면 다시 수집하기 위한 키. */
  docKey: string;
}

export default function ManualToc({ containerRef, docKey }: ManualTocProps) {
  const [entries, setEntries] = useState<TocEntry[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  /** 스크롤이 바닥에 닿아 있는 동안 true — IntersectionObserver의 갱신을 눌러 경합을 막는다. */
  const atBottomRef = useRef(false);

  // 문서 전환 시 제목 수집 (렌더 직후 + 다음 프레임 한 번 더 — 늦게 붙는 노드 대비)
  useEffect(() => {
    let frame = 0;

    const collect = () => {
      const root = containerRef.current;
      if (!root) return;
      const found: TocEntry[] = Array.from(root.querySelectorAll('h2[id], h3[id]')).map((el) => ({
        id: el.id,
        text: el.textContent?.trim() ?? '',
        level: el.tagName === 'H3' ? 3 : 2,
      }));
      setEntries((prev) => {
        const same =
          prev.length === found.length && prev.every((entry, i) => entry.id === found[i].id);
        return same ? prev : found;
      });
    };

    collect();
    frame = requestAnimationFrame(collect);
    return () => cancelAnimationFrame(frame);
  }, [containerRef, docKey]);

  // 현재 위치 하이라이트
  useEffect(() => {
    if (entries.length === 0) {
      setActiveId(null);
      return;
    }

    const elements = entries
      .map((entry) => document.getElementById(entry.id))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return;

    setActiveId((prev) => prev ?? entries[0].id);

    const visible = new Set<string>();
    /** 관측 띠(상단 30%) 안에서 문서 순서상 가장 위에 있는 제목. */
    const firstInBand = () => entries.find((entry) => visible.has(entry.id))?.id;

    const observer = new IntersectionObserver(
      (records) => {
        for (const record of records) {
          if (record.isIntersecting) visible.add(record.target.id);
          else visible.delete(record.target.id);
        }
        // 바닥에 닿아 있는 동안은 아래 스크롤 핸들러가 마지막 제목을 고정한다(경합 방지).
        if (atBottomRef.current) return;
        const first = firstInBand();
        if (first) setActiveId(first);
      },
      // 상단 근처에 들어온 제목만 활성 — 화면 하단 제목이 먼저 잡히는 것을 막는다.
      // 띠 밖(문서 끝쪽) 제목이 도달하지 못하는 문제는 본문 끝 여백(computeTailSpacer)이 해소한다.
      { rootMargin: TOC_ROOT_MARGIN, threshold: 0 },
    );

    elements.forEach((el) => observer.observe(el));

    // ⚠️ 바닥 보정 — 위 rootMargin이 관측 띠를 상단 30%로 좁히기 때문에, **마지막 제목들은 그 띠에
    // 영원히 도달하지 못한다**(페이지 끝에서 스크롤이 멈춰 더 밀어 올릴 여백이 없다). 그래서 끝까지
    // 내려도 활성 표시가 중간 제목에 멈춘다. 문서가 짧아서가 아니라, 마지막 제목 뒤 내용이 화면
    // 높이의 70%보다 짧으면 **어느 문서에서든** 발생한다. (2026-07-23 사용자 제보)
    const scroller = findScrollParent(containerRef.current);
    let frame = 0;

    const syncBottom = () => {
      frame = 0;
      const scrollTop = scroller ? scroller.scrollTop : window.scrollY;
      const viewport = scroller ? scroller.clientHeight : window.innerHeight;
      const total = scroller ? scroller.scrollHeight : document.documentElement.scrollHeight;

      const atBottom = isScrolledToBottom({ scrollTop, viewport, total });

      if (atBottom === atBottomRef.current) return;
      atBottomRef.current = atBottom;
      if (atBottom) setActiveId(entries[entries.length - 1].id);
      else {
        const first = firstInBand();
        if (first) setActiveId(first);
      }
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(syncBottom);
    };

    const target: HTMLElement | Window = scroller ?? window;
    target.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    syncBottom();

    return () => {
      observer.disconnect();
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
      atBottomRef.current = false;
    };
  }, [entries, containerRef]);

  if (entries.length === 0) return null;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    const target = document.getElementById(id);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  };

  return (
    <nav aria-label="문서 목차" className="sticky top-0 max-h-[calc(100vh-8rem)] overflow-y-auto">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">이 문서에서</p>
      <ul className="space-y-1 border-l border-gray-200 text-sm">
        {entries.map((entry) => (
          <li key={entry.id}>
            <a
              href={`#${entry.id}`}
              onClick={(event) => handleClick(event, entry.id)}
              aria-current={activeId === entry.id ? 'true' : undefined}
              className={`block border-l-2 py-0.5 hover:text-gray-900 ${
                entry.level === 3 ? 'pl-6' : 'pl-3'
              } ${
                activeId === entry.id
                  ? 'border-red-500 font-medium text-gray-900'
                  : 'border-transparent text-gray-500'
              }`}
            >
              {entry.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
